import { Link } from "@reach/router";
import moment from "moment";
import React from "react";
import ReactSVG from "react-svg";
import {
  Box,
  BoxProps,
  forwardRef,
  Heading,
  HeadingProps,
  HStack,
  ImageProps,
  StackProps,
  Text,
  TextProps,
  Image,
  Link as ChakraLink,
  Flex,
  Tag,
  TagLabel, Grid, Avatar,
} from "@chakra-ui/react";
import { MetaCard } from "@components/Cards/MetaCard/MetaCard";
import {
  AvatarCard,
  AVATAR_CARD_MODES,
} from "@components/Cards/AvataraCard/AvatarCard";
import { generatePostUrl, preparePostUrl } from "@core/utils";
import { WidgetTitle } from "@components/Texts/WidgetTitle/WidgetTitle";
import share from '../../../images/share.svg'

moment.locale("pl");

export const prepareExcerpt = (text, maxLength = 100) => {
  if (text.length > maxLength) {
    const shortText = text.slice(0, maxLength + 5).split(' ').slice(0, -1).join(' ');
    return `${shortText}...`;
  }
  return text;
}

export const TileContainer = forwardRef<BoxProps, "div">((props, ref) => (
  <Box ref={ref} {...props} />
));

interface IImageProps extends ImageProps {
  imageH?: any;
}

export const TileImage = forwardRef<IImageProps, "img">((props, ref) => (
  <Image maxH="144px" w="100%" loading="lazy"
         sx={{'background': 'linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 29.46%), url(image.png)'}}
         mb="8px" objectFit="cover" ref={ref} {...props} />
));

export const TileAvatar = forwardRef<BoxProps, "div">((props, ref) => (
  <AvatarCard mb="10px" ref={ref} mode={AVATAR_CARD_MODES.SHORT} {...props} />
));

export const TileTitle = forwardRef<HeadingProps, "h2">((props, ref) => (
  <Heading fontSize={{ base: "14px", sm: "18px" }} mb="8px" ref={ref} {...props} />
));

export const TileDescription = forwardRef<TextProps, "div">(({ excerpt, ...props }: any, ref) => (
  <Box
    mb="10px" sx={{
    'a.more-link': {
      'display': 'none',
    }
  }} dangerouslySetInnerHTML={{ __html: excerpt }} ref={ref} {...props} />
));

export const TilePublicationDate = forwardRef<TextProps, "p">((props, ref) => (
  <Text ref={ref} {...props} />
));

export const TileReadingTime = forwardRef<TextProps, "p">((props, ref) => (
  <Text ref={ref} {...props} />
));

export const TileMeta = forwardRef<StackProps & {isDate?: boolean}, "div">((props, ref) => (
  <MetaCard isDate={props.isDate} fontSize="10px" ref={ref} {...props} />
));

export enum TILE_MODES {
  DESCRIPTIVE_WITH_THUMBNAIL,
  DESCRIPTIVE_WITHOUT_THUMBNAIL,
  SHORT_WITH_THUMBNAIL,
  SHORT_WITH_THUMBNAIL_WITH_AVATAR,
  SHORT_WITHOUT_THUMBNAIL,
  SHORT_WITHOUT_THUMBNAIL_AND_MARGIN,
  DESCRIPTIVE_TEXT,
  PRIMARY,
  TITLE,
  SHORT_WITH_THUMBNAIL_WITH_AVATAR_MOBILE,
  SHORT_MOBILE_THUMBNAIL_RIGHT
}

const tileModeOptions = {
  [TILE_MODES.SHORT_MOBILE_THUMBNAIL_RIGHT]: ({
    article,
    ...props
  }) => {
    if(!article) {
      return null
    }
    const { categories, author, title, featuredImage, excerpt, slug } = article
    return (
      <TileContainer as={Link} to={preparePostUrl(article)} {...props}>
        <Grid alignItems="center" height="100%" templateColumns="65% 35%" gridGap="10px">
          <Flex flexDirection="column" height="100%" justifyContent="space-between">
            <TileAvatar fontSize="8px" color="#C4C4C4" author={author} />
            <TileTitle fontSize="14px">{title}</TileTitle>
            <TileMeta isDate={props.isDate} fontSize="10px" article={article} />
          </Flex>
          <Box>
            <TileImage margin="0" height="100%" src={featuredImage?.node?.blogBig} />
          </Box>
        </Grid>
      </TileContainer>
    )
  },
  [TILE_MODES.DESCRIPTIVE_WITH_THUMBNAIL]: ({
    article,
    ...props
  }) => {
    if(!article) {
      return null
    }
    const { categories, author, title, featuredImage, excerpt, slug } = article;
    return (
      <TileContainer as={Link} to={preparePostUrl(article)} {...props}>
        <TileImage src={featuredImage?.node?.blogBig} />
        <TileAvatar author={author} />
        <TileTitle>{title}</TileTitle>
        <TileDescription excerpt={prepareExcerpt(excerpt)} />
        <TileMeta isDate={props.isDate} mb={{base: 0, md: "35px"}} article={article} />
      </TileContainer>
    )
  },
  [TILE_MODES.DESCRIPTIVE_WITHOUT_THUMBNAIL]: ({
    article,
    ...props
  }) => {
    if(!article) {
      return null
    }
    const { categories, author, title, excerpt, slug } = article;
    return (
      <TileContainer as={Link} to={preparePostUrl(article)} {...props}>
        <TileAvatar author={author} />
        <TileTitle>{title}</TileTitle>
        <TileDescription excerpt={prepareExcerpt(excerpt)} />
        <TileMeta isDate={props.isDate} article={article} />
      </TileContainer>
    )
  },
  [TILE_MODES.SHORT_WITH_THUMBNAIL]: ({
    article,
    imageH,
    ...props
  }) => {
    if(!article) {
      return null
    }
    const { categories, author, slug, title, featuredImage } = article;
    return (
      <TileContainer as={Link} to={preparePostUrl(article)} {...props}>
        <TileImage maxH={imageH || "144px"} src={featuredImage?.node?.blogBig} />
        {/* <TileAvatar author={author} /> */}
        <TileTitle>{title}</TileTitle>
        <TileMeta isDate={props.isDate} article={article} />
      </TileContainer>
    )
  },
  [TILE_MODES.SHORT_WITH_THUMBNAIL_WITH_AVATAR]: ({
    article,
    imageH,
    ...props
  }) => {
    if(!article) {
      return null
    }
    const { categories, author, slug, title, featuredImage } = article;
    return (
      <TileContainer as={Link} to={preparePostUrl(article)} {...props}>
        <TileImage maxH={imageH || "144px"} src={imageH === "310px" ? featuredImage?.node?.blogBig : featuredImage?.node?.blogBig} />
        <TileAvatar author={author} />
        <TileTitle>{title}</TileTitle>
        <TileMeta isDate={props.isDate} article={article} />
      </TileContainer>
    )
  },
  [TILE_MODES.SHORT_WITH_THUMBNAIL_WITH_AVATAR_MOBILE]: ({
    article,
    imageH,
    ...props
  }) => {
    if(!article) {
      return null
    }
    const { categories, author, slug, title, featuredImage } = article;
    return (
      <TileContainer position="relative" as={Link} to={preparePostUrl(article)} {...props}>
        <TileImage maxH={imageH || "144px"} src={featuredImage?.node?.blogBig} />

        {/*{ props.showOnlyAvatar ?*/}
        {/*  <Avatar*/}
        {/*    size='sm'*/}
        {/*    src={author.node.avatar.url}*/}
        {/*    alt="avatar"*/}
        {/*    left="15px"*/}
        {/*    top="10px"*/}
        {/*    position="absolute"*/}
        {/*    mr="1rem"*/}
        {/*  />*/}
        {/*  : <TileAvatar left="15px" top="10px" color="white" position="absolute" author={author} />*/}
        {/*}*/}
        <TileTitle>{title}</TileTitle>
        <TileMeta isDate={props.isDate} article={article} />
      </TileContainer>
    )
  },
  [TILE_MODES.SHORT_WITHOUT_THUMBNAIL]: ({
    article,
    ...props
  }) => {
    if(!article) {
      return null
    }
    const { categories, author, title, slug } = article;

    return (
      <TileContainer as={Link} to={preparePostUrl(article)} {...props}>
        <TileAvatar author={author} />
        <TileTitle>{title}</TileTitle>
        <TileMeta isDate={props.isDate} mb={{base: 0, md: "35px"}} article={article} />
      </TileContainer>
    )
  },
  [TILE_MODES.SHORT_WITHOUT_THUMBNAIL_AND_MARGIN]: ({
    article,
    ...props
  }) => {
    if(!article) {
      return null
    }
    const { categories, author, title, slug } = article;
    return (
      <TileContainer as={Link} to={preparePostUrl(article)} {...props}>
        <TileAvatar author={author} />
        <TileTitle>{title}</TileTitle>
        <TileMeta isDate={props.isDate} mb="0" article={article} />
      </TileContainer>
    )
  },
  [TILE_MODES.DESCRIPTIVE_TEXT]: ({
    article,
    ...props
  }) => {
    if(!article) {
      return null
    }
    const { categories, author, excerpt, title, slug } = article;
    return (
      <TileContainer as={Link} to={preparePostUrl(article)} {...props}>
        <TileTitle>{title}</TileTitle>
        <TileDescription fontSize="16px" mb="20px" excerpt={prepareExcerpt(excerpt)} />
      </TileContainer>
    )
  },
  [TILE_MODES.TITLE]: ({
    article,

    ...props
  }) => {
    if(!article) {
      return null
    }
    const { categories, author, title, slug } = article
    return (
      <TileContainer as={Link} to={preparePostUrl(article)} {...props}>
        <TileTitle>{title}</TileTitle>
      </TileContainer>
    )
  },
  [TILE_MODES.PRIMARY]: ({
    category,
    article,
    ...props
  }) => {
    // console.warn(article)
    if(!article) {
      return null
    }
    const { categories: { nodes: [firstCategory] }, categories, author, title, featuredImage, excerpt, slug } = article;
    return (
      <TileContainer as={article.databaseId === 14670 ? ChakraLink : Link} isExternal href="https://sklep.fundacjaernesta.pl" to={preparePostUrl(article)} {...props}>
        <Box position="relative" mx={{ base: '-15px', sm: '0' }}>
          <TileImage background="linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);" src={featuredImage?.node?.blogBig} maxH="280px" mb="18px"/>
          {/* <Tag position="absolute" top="28px" right="13px"
              size="sm" borderRadius="none" variant="solid" color="#fff" cursor="pointer"
              backgroundColor="#F970FC" px="8px" py="6px" mr="5px" mb="5px">
            <TagLabel textTransform="uppercase" fontSize="12px" fontWeight="700">Pilne</TagLabel>
          </Tag> */}
          <Box d={{base: 'block', sm: 'none'}} position="absolute" background="linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);" top="0" left="0" w="100%" h="100%"/>


          <Tag position="absolute" top={{base: '25px', sm: "28px"}} right={{base: 0, sm:'-11px'}}
              size="sm" borderRadius="none" variant="solid" color="#222C63" cursor="pointer"
              backgroundColor="#D1E6FF" px="8px" py="6px" mr="5px" mb="5px">
            <TagLabel textTransform="uppercase" fontSize="12px" fontWeight="700">Wiadomość dnia</TagLabel>
          </Tag>
          <Box position="absolute" d={{base: 'block', sm: 'none'}} bottom="32px" left="15px">
            <Text fontSize="12px" lineHeight="14px" letterSpacing="0.1em" color="rgba(255, 255, 255, 0.5)">{firstCategory.name}</Text>
            <TileTitle maxW="70%" color="white" fontSize="18px" mb="14px">{title}</TileTitle>
          </Box>
        </Box>

        <Box d={{base: 'none', sm: 'block'}}>
          <Flex justifyContent="space-between" mb="18px">
            {firstCategory && (<WidgetTitle>{firstCategory.name}</WidgetTitle>)}
            <TileMeta isDate={props.isDate} fontSize="14px" article={article} />
          </Flex>
          <TileTitle fontSize="26px" mb="14px">{title}</TileTitle>
          <TileDescription fontSize="16px" mb="20px" excerpt={prepareExcerpt(excerpt, 300)} />
          <TileAvatar author={author} mode={AVATAR_CARD_MODES.FULL} />
        </Box>
      </TileContainer>
    );
  }
};

export const Tile = forwardRef<BoxProps, "div">(({ mode, ...props }: any, ref) =>
  tileModeOptions[mode]({ ...props, ref })
);
